import {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse
} from 'axios'
import mockAxiosInENV from '@/__mocks__/config'

mockAxiosInENV('mock')

export type ResponseMapper = (response: AxiosResponse) => unknown
export type ErrorMapper = (error: AxiosError) => Promise<unknown> | unknown

const baseApiService = function (
  axiosInstance: AxiosInstance,
  responseMapper: ResponseMapper,
  errorMapper?: ErrorMapper
) {
  const onSuccess = <T>(response: AxiosResponse): T => {
    return responseMapper(response) as T
  }

  const onError = async (error: AxiosError) => {
    const code = error.code
    const status = error?.response?.status
    const details = errorMapper
      ? await errorMapper(error)
      : error?.response?.data
    const requestUrl = error?.response?.request.responseURL
    return Promise.reject({ code, status, details, requestUrl })
  }

  return {
    httpGET<U = unknown>(
      resource: string,
      config?: AxiosRequestConfig,
      responseMapperOverride?: ResponseMapper
    ) {
      return axiosInstance
        .get<U>(`${resource}`, config)
        .then((r) =>
          responseMapperOverride
            ? (responseMapperOverride(r) as U)
            : onSuccess<U>(r)
        )
        .catch(onError)
    },

    httpPOST<U = unknown, D = unknown>(
      resource: string,
      body?: D,
      config?: AxiosRequestConfig,
      responseMapperOverride?: ResponseMapper
    ) {
      return axiosInstance
        .post<U, AxiosResponse<U>, D>(`${resource}`, body, config)
        .then((r) =>
          responseMapperOverride
            ? (responseMapperOverride(r) as U)
            : onSuccess<U>(r)
        )
        .catch(onError)
    },

    httpFormData<U = unknown>(
      resource: string,
      body?: FormData,
      config?: AxiosRequestConfig
    ) {
      return axiosInstance
        .postForm<U>(`${resource}`, body, config)
        .then((r) => onSuccess<U>(r))
        .catch(onError)
    },

    httpPATCH<U = unknown>(
      resource: string,
      body?: unknown,
      config?: AxiosRequestConfig
    ) {
      return axiosInstance
        .patch<U>(`${resource}`, body, config)
        .then((r) => onSuccess<U>(r))
        .catch(onError)
    },

    httpUPDATE<U = unknown>(
      resource: string,
      slug = '',
      body?: Record<string, unknown>
    ) {
      return axiosInstance
        .put<U>(`${resource}/${slug}`, body)
        .then((r) => onSuccess<U>(r))
        .catch(onError)
    },

    httpPUT<U = unknown, D = unknown>(resource: string, body?: D) {
      return axiosInstance
        .put<U>(`${resource}`, body)
        .then((r) => onSuccess<U>(r))
        .catch(onError)
    },

    httpDELETE<U = unknown>(resource: string) {
      return axiosInstance
        .delete<U>(resource)
        .then((r) => onSuccess<U>(r))
        .catch(onError)
    }
  }
}

export default baseApiService
